/**
 * Naming the environment an Estimated value row belongs to.
 *
 * A row is one Environment, and carries the runtime that Environment reported.
 * The first label that knows the runtime wins, in the order a reader would
 * trust: the environment that reported the row in this very page load, then
 * CMMD's device registry, the only label that survives the machine being off,
 * then the saved environment registry, then the raw id. Pure, so the order can
 * be tested without stores.
 *
 * @module usage/usageEnvironmentLabels
 */
import type { EnvironmentId, UsageSummary } from "@cmmd-forge/contracts";
import type { EnvironmentTotals } from "@cmmd-forge/shared/usageMerge";

import { buildCmmdRuntimeEnvironmentId } from "../environments/cmmdRuntimeEnvironmentId";

export interface UsageEnvironmentLabelContext {
  /** Runtime ids named by a recorded or transcripts fingerprint, to the source's label. */
  readonly labelByReportedRuntimeId: ReadonlyMap<string, string>;
  /** CMMD's desktop device registry, keyed by runtime id. */
  readonly desktopLabelByRuntimeId: ReadonlyMap<string, string>;
  /** CMMD's runtime inventory, keyed by runtime id. */
  readonly inventoryLabelByRuntimeId: ReadonlyMap<string, string>;
  /** Saved environments, keyed by environment id. */
  readonly savedLabelByEnvironmentId: ReadonlyMap<string, string>;
  /** Usage sources on this page, keyed by source id. */
  readonly sourceLabelById: ReadonlyMap<string, string>;
}

/**
 * Runtime ids each answering source spoke for, from its own fingerprints.
 *
 * The merge reads the same fingerprints, in `claimRuntimes`, to decide which
 * source a runtime-keyed bucket belongs to. Keep the two rules in step: both
 * skip CMMD's fingerprint, because CMMD speaks for no machine.
 */
export function labelReportedRuntimes(
  sources: ReadonlyArray<{ readonly label: string; readonly summary: UsageSummary | null }>,
): ReadonlyMap<string, string> {
  const labels = new Map<string, string>();
  for (const source of sources) {
    for (const reported of source.summary?.sources ?? []) {
      if (reported.fingerprint.kind === "cmmd") continue;
      for (const runtimeId of reported.runtimeIds) {
        if (!labels.has(runtimeId)) labels.set(runtimeId, source.label);
      }
    }
  }
  return labels;
}

/** A runtime id short enough to sit in a table cell, when nothing names it. */
export function shortRuntimeId(runtimeId: string): string {
  const bare = runtimeId.replace(/^runtime_/, "");
  return `Runtime ${bare.slice(0, 8)}`;
}

export function resolveUsageEnvironmentLabel(
  row: Pick<EnvironmentTotals, "runtimeId" | "sourceId">,
  context: UsageEnvironmentLabelContext,
): string {
  const { runtimeId } = row;
  if (runtimeId === null) return context.sourceLabelById.get(row.sourceId) ?? row.sourceId;
  return (
    context.labelByReportedRuntimeId.get(runtimeId) ??
    context.desktopLabelByRuntimeId.get(runtimeId) ??
    context.inventoryLabelByRuntimeId.get(runtimeId) ??
    context.savedLabelByEnvironmentId.get(buildCmmdRuntimeEnvironmentId(runtimeId)) ??
    context.savedLabelByEnvironmentId.get(runtimeId as EnvironmentId) ??
    shortRuntimeId(runtimeId)
  );
}

/** The registries the page has in hand, before they are indexed by runtime id. */
export interface UsageEnvironmentLabelInputs {
  readonly sources: ReadonlyArray<{
    readonly sourceId: string;
    readonly label: string;
    readonly summary: UsageSummary | null;
  }>;
  readonly desktopRuntimes: ReadonlyArray<
    readonly [key: string, group: { readonly label: string }]
  >;
  readonly inventoryRuntimes: ReadonlyArray<{
    readonly runtime: { readonly id: string; readonly displayName: string };
  }>;
  readonly savedEnvironments: ReadonlyArray<{
    readonly environmentId: string;
    readonly label: string;
  }>;
}

export function buildUsageEnvironmentLabelContext(
  inputs: UsageEnvironmentLabelInputs,
): UsageEnvironmentLabelContext {
  return {
    labelByReportedRuntimeId: labelReportedRuntimes(inputs.sources),
    desktopLabelByRuntimeId: new Map(
      inputs.desktopRuntimes.map(([runtimeId, group]) => [String(runtimeId), group.label]),
    ),
    inventoryLabelByRuntimeId: new Map(
      inputs.inventoryRuntimes.map((entry) => [entry.runtime.id, entry.runtime.displayName]),
    ),
    savedLabelByEnvironmentId: new Map(
      inputs.savedEnvironments.map((record) => [String(record.environmentId), record.label]),
    ),
    sourceLabelById: new Map(inputs.sources.map((source) => [source.sourceId, source.label])),
  };
}

/** The three registry lists as arrays, empty until the registry has loaded. */
export function deviceRegistryLists<TRuntime, TDevice, THost>(
  registry:
    | {
        readonly runtimes?: readonly TRuntime[];
        readonly devices?: readonly TDevice[];
        readonly hosts?: readonly THost[];
      }
    | null
    | undefined,
): {
  readonly runtimes: readonly TRuntime[];
  readonly devices: readonly TDevice[];
  readonly hosts: readonly THost[];
} {
  return {
    runtimes: registry?.runtimes ?? [],
    devices: registry?.devices ?? [],
    hosts: registry?.hosts ?? [],
  };
}
